// ui.ts — tiny shared UI primitives for the M3 meta screens (stats panel, pause menu, results,
// leaderboard). Keeps the new scenes DRY and legible at 360x640 without re-implementing buttons,
// modal veils, and stat rows in each scene. Pure Phaser GameObjects + the shared palette/font; no
// Core, no network, no determinism touch.
import Phaser from 'phaser';
import { PALETTE, hex, FONT } from './keys';
import { audio } from './audio';
import { motionAllowed } from './settings';
import { typePx } from './ui/typeScale';
import { paintButtonBg, shade } from './theme';
import { icon, drawIcon } from './icons';

export interface ButtonOpts {
  fill?: number;
  textColor?: number;
  stroke?: number;
  fontScale?: number; // multiplier on the size-from-width
  sfx?: 'uiTap' | 'uiConfirm' | null;
  width?: number;
  height?: number;
}

/** A tappable button with hover/press feedback. Returns the container so callers can position/depth. */
export function button(
  scene: Phaser.Scene,
  x: number,
  y: number,
  label: string,
  onClick: () => void,
  opts: ButtonOpts = {},
): Phaser.GameObjects.Container {
  const W = scene.scale.width;
  const w = opts.width ?? Math.min(W * 0.86, 460);
  const h = opts.height ?? 46;
  const base = opts.fill ?? PALETTE.panel;
  const textColor = opts.textColor ?? PALETTE.text;
  const c = scene.add.container(x, y);
  const g = scene.add.graphics();
  paintButtonBg(g, w, h, base, {});
  const txt = scene.add
    .text(0, 0, label, {
      fontFamily: FONT.display,
      // max 36·fontScale (not 22): HEAD was `scaledFont(max(14, W*0.028*fontScale), 13)` — unclamped, so
      // at W=1280 it read ~36px. A 22 ceiling broke desktop size-neutrality (buttons shrank); restore it.
      fontSize: `${typePx(scene, 'body', { vw: 0.028 * (opts.fontScale ?? 1), min: 14, max: 36 * (opts.fontScale ?? 1) })}px`,
      color: hex(textColor),
      fontStyle: 'bold',
      align: 'center',
    })
    .setOrigin(0.5);
  const hit = scene.add.rectangle(0, 0, w, h, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
  c.add([g, txt, hit]);
  hit.on('pointerover', () => paintButtonBg(g, w, h, shade(base, 0.08), {}));
  hit.on('pointerout', () => paintButtonBg(g, w, h, base, {}));
  hit.on('pointerdown', () => {
    paintButtonBg(g, w, h, base, { pressed: true });
    txt.setY(1);
  });
  hit.on('pointerup', () => {
    paintButtonBg(g, w, h, base, {});
    txt.setY(0);
    if (opts.sfx !== null) audio().playSfx(opts.sfx ?? 'uiConfirm');
    onClick();
  });
  return c;
}

/** A full-screen modal: dark veil (tap-to-close optional) + a centered card container. */
export interface Modal {
  layer: Phaser.GameObjects.Container;
  card: Phaser.GameObjects.Container;
  cardW: number;
  cardH: number;
  /** local y just below the header divider — where panel body content should start. */
  bodyTop: number;
  /** local y where a pinned full-width footer CTA (DONE) should sit. */
  footerY: number;
  close: () => void;
  /**
   * S3b gap 3 — CONTENT-HUGGING RESIZE for full-bleed meta panels whose content extent is only known
   * after async data lands (leaderboard rows, ghost-ladder rungs). Shrinks the card to hug
   * `contentHeight` (measured from bodyTop) while keeping the card's TOP edge FIXED — so already-placed
   * top-anchored content never shifts, and only the bottom edge + footer move up under the last row,
   * killing the dead band. Returns the fresh geometry; `bodyTop` is unchanged, `footerY` moves up.
   * No-op on centered (non-full-bleed) modals. Safe to call repeatedly (e.g. on every tab switch).
   */
  relayout: (contentHeight: number) => { cardH: number; bodyTop: number; footerY: number };
}

// Open-modal refcount so DOM overlays (the menu's topic <input>, which sits ABOVE the canvas and can't
// be covered by a Phaser veil) can hide themselves while any modal is up. Tracked off the layer's
// DESTROY event so it stays correct no matter how a panel is dismissed (close(), veil-tap, or a raw
// layer.destroy()).
let openModalCount = 0;
export function anyModalOpen(): boolean {
  return openModalCount > 0;
}

/**
 * The menu's topic <input> is a DOM overlay ABOVE the canvas, so a Phaser veil cannot cover it.
 * Hide any DOM inputs SYNCHRONOUSLY when a modal/sheet opens so the placeholder never bleeds through
 * (the menu's positionInput poll restores them once anyModalOpen() goes false on close). No-op when
 * there is no DOM input (in-game modals).
 */
function hideDomInputsForModal(): void {
  try {
    document.querySelectorAll('#game input, body > input, input').forEach((el) => {
      (el as HTMLElement).style.display = 'none';
    });
  } catch {
    /* SSR/headless without DOM — nothing to hide */
  }
}

/**
 * Non-modal overlays (the onboarding tutorial) that sit over the canvas can also be bled through by
 * the menu's DOM topic <input>. They call pushInputBlocker()/popInputBlocker() to count as "a modal
 * is open" for input-hiding (anyModalOpen() → true), so the menu's positionInput poll keeps the
 * input hidden for the overlay's lifetime, then restores it on pop.
 */
export function pushInputBlocker(): void {
  openModalCount++;
  hideDomInputsForModal();
}
export function popInputBlocker(): void {
  openModalCount = Math.max(0, openModalCount - 1);
}

export function modal(
  scene: Phaser.Scene,
  opts: {
    title: string;
    widthFrac?: number;
    heightFrac?: number;
    onClose?: () => void;
    veilClose?: boolean;
    /** Top-rail color stops (≥1). 1 stop = solid; ≥2 = left→right gradient. Default [scan]. */
    railGrad?: number[];
    /** Full-bleed inset card (the meta-panel pattern): large, top-anchored, ~3% side margin. */
    fullBleed?: boolean;
    /**
     * Force the top-right ✕ close even on a title-LESS modal. A panel that draws its OWN header (e.g.
     * Custom Lab's flask + "CUSTOM LAB") passes title:'' yet still needs the ✕; set this true there.
     * Title-less modals that are intentionally headerless (offline, today's-gauntlet) leave it unset.
     */
    showClose?: boolean;
    /** Optional fixed card width for a placed, content-hugging panel. Existing callers omit it. */
    widthPx?: number;
    /** Optional absolute x center for the card; used by desktop side panels. */
    centerX?: number;
    /**
     * S3 CONTENT-HUGGING: the px height of the body content the caller will fill BELOW the header. When
     * set, the card sizes to exactly fit (header + contentHeight + padding) instead of a fixed fraction
     * or the full screen — killing the >20% dead band on meta surfaces. Top-anchored like a full-bleed
     * sheet (with the same side margin) so it still reads as a panel, just no wasted band. Clamped to
     * the viewport; a huge content height falls back to the tallest that fits.
     */
    contentHeight?: number;
  } = {
    title: '',
  },
): Modal {
  const W = scene.scale.width;
  const H = scene.scale.height;
  const fb = !!opts.fullBleed;
  // Content-hugging mode borrows the full-bleed side margins + top anchor but sizes the card height to
  // its content. It supersedes fullBleed's full-height fill when contentHeight is provided.
  const hug = opts.contentHeight !== undefined;
  const marginX = fb || hug ? Math.round(W * 0.026) : 0;
  const topPad = fb || hug ? Math.round(H * 0.034) : 0;
  // S9 FIX-C (G0 AMATEUR 2, systemic): a hard 18px floor — H*0.02 is only 16px at 800h, which let panel
  // content sit visually flush with the viewport bottom on desktop (today-gauntlet sublines).
  const botPad = fb || hug ? Math.max(18, Math.round(H * 0.02)) : 0;
  const defaultW = fb || hug ? W - marginX * 2 : Math.min(W * (opts.widthFrac ?? 0.9), 440);
  const cardW = opts.widthPx !== undefined ? Math.min(W - marginX * 2, opts.widthPx) : defaultW;
  const hasHeaderPre = String(opts.title || '').trim().length > 0;
  // Header band height (title + divider) that content sits below; 0 for a headerless hug card.
  const headerBand = hasHeaderPre ? 60 : 20;
  const cardH = hug
    ? Math.min(H - topPad - botPad, headerBand + (opts.contentHeight ?? 0) + Math.round(H * 0.028))
    : fb
      ? H - topPad - botPad
      : Math.min(H * (opts.heightFrac ?? 0.78), 620);
  // S8 #24 SYSTEMIC MODAL FIX: centre a fullBleed / content-hugged card in the viewport whenever it is
  // SHORTER than the available space — killing the 35–55% dead veil band that top-anchoring dumped below
  // short panels (Settings/Stats/Store/Locker/Leaderboard/Daily/Ghost) on tall phones. A card that truly
  // fills the height (cardH ≈ available) clamps back to the top anchor (min>max ⇒ Clamp returns the min).
  // Applies to BOTH orientations now (was landscape-only), and is re-applied by relayout()/the auto-hug
  // pass below as the card shrinks to its real content. Plain centred dialogs (no fb/hug) are unchanged.
  const cyFor = (h: number): number =>
    fb || hug ? Phaser.Math.Clamp(H / 2, topPad + h / 2, H - botPad - h / 2) : H / 2;
  const cy = cyFor(cardH);

  const layer = scene.add.container(0, 0).setDepth(30_000);
  openModalCount++;
  audio().playPanelOpen(); // #61: the shared panel-open whoosh (fires as any modal()/sheet() surface opens)
  hideDomInputsForModal();
  layer.once(Phaser.GameObjects.Events.DESTROY, () => {
    openModalCount = Math.max(0, openModalCount - 1);
  });
  // Veil: deep `void` — focuses the modal. Full-bleed panels inset a thin top band ABOVE the card; a
  // translucent veil there let the bright menu chrome (LV rank badge / missions chip / icon row) ghost
  // through ~3%, so the full-bleed veil is fully OPAQUE. Centered dialogs keep 0.97 (no exposed band).
  const veil = scene.add.rectangle(W / 2, H / 2, W, H, PALETTE.void, fb ? 1 : 0.97).setInteractive();
  const card = scene.add.container(opts.centerX ?? W / 2, cy);
  // Body = surface2 (raised/modal), hairline lineStrong border, radius via a rounded graphic. Drawn
  // from a FIXED top edge (topLocal) with a variable height so a content-hugging relayout can pull the
  // bottom up without shifting any top-anchored content (S3b gap 3).
  const bg = scene.add.graphics();
  const r = 16;
  const topLocal = -cardH / 2;
  const drawBg = (h: number): void => {
    bg.clear();
    bg.fillStyle(PALETTE.surface2, 1);
    bg.fillRoundedRect(-cardW / 2, topLocal, cardW, h, r);
    bg.lineStyle(1.5, PALETTE.lineStrong, 1);
    bg.strokeRoundedRect(-cardW / 2, topLocal, cardW, h, r);
  };
  drawBg(cardH);
  // Brand signature: a 4px top-rail spanning the card top. Default `scan`; per-screen variants
  // (gold→armed achievements, player→rival→gold credits, solid-hazard confirm) pass railGrad.
  const topRail = scene.add.graphics();
  // Omitted → default [scan]; an explicit [] suppresses the rail entirely (title-less full-screens).
  const stops = opts.railGrad === undefined ? [PALETTE.scan] : opts.railGrad;
  if (stops.length === 1) {
    topRail.fillStyle(stops[0], 0.95);
    topRail.fillRoundedRect(-cardW / 2, -cardH / 2, cardW, 4, { tl: r, tr: r, bl: 0, br: 0 });
  } else if (stops.length >= 2) {
    const segs = 48;
    const segW = cardW / segs;
    for (let i = 0; i < segs; i++) {
      const f = i / (segs - 1);
      const span = f * (stops.length - 1);
      const a = stops[Math.floor(span)];
      const b = stops[Math.min(stops.length - 1, Math.floor(span) + 1)];
      const lt = span - Math.floor(span);
      const col = Phaser.Display.Color.Interpolate.ColorWithColor(
        Phaser.Display.Color.IntegerToColor(a),
        Phaser.Display.Color.IntegerToColor(b),
        100,
        Math.round(lt * 100),
      );
      topRail.fillStyle(Phaser.Display.Color.GetColor(col.r, col.g, col.b), 0.95);
      topRail.fillRect(-cardW / 2 + i * segW, -cardH / 2, segW + 1, 4);
    }
  }
  // Header (≈44): title (display face, upper) + ✕ close, then a divider hairline. A title-LESS modal
  // (e.g. the offline full-screen state) renders HEADERLESS — no divider, no ✕ — matching the ref,
  // which shows a clean full-screen panel with its own in-body action (no stray header chrome).
  const hasHeader = String(opts.title || '').trim().length > 0;
  const headerY = -cardH / 2 + (hug ? 32 : 26);
  card.add([bg, topRail]);
  if (hasHeader) {
    const titlePx = typePx(scene, 'title', { vw: 0.032, min: 17, max: hug ? 24 : 30 });
    const title = scene.add
      .text(-cardW / 2 + 22, headerY, opts.title!.toUpperCase(), {
        fontFamily: FONT.display,
        fontSize: `${titlePx}px`,
        color: hex(PALETTE.text),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
    const divider = scene.add.rectangle(0, -cardH / 2 + (hug ? 54 : 48), cardW - 36, 1, PALETTE.line).setOrigin(0.5, 0.5);
    card.add([title, divider]);
  }
  layer.add([veil, card]);

  const close = (): void => {
    layer.destroy();
    opts.onClose?.();
  };
  if (opts.veilClose !== false) veil.on('pointerup', close);

  // ✕ close affordance, top-right of the header (≥44 hit area). Shown for any titled modal AND any
  // panel that draws its own header but opts in via showClose (Custom Lab). Intentionally-headerless
  // title-less modals (offline, today's-gauntlet) omit it.
  if (hasHeader || opts.showClose) {
    const xc = cardW / 2 - 24;
    const xIcon = icon(scene, 'x', xc, headerY, 18, PALETTE.dim);
    const xHit = scene.add.rectangle(xc, headerY, 44, 44, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
    xHit.on('pointerover', () => {
      xIcon.clear();
      drawIcon(xIcon, 'x', 18, PALETTE.text);
    });
    xHit.on('pointerout', () => {
      xIcon.clear();
      drawIcon(xIcon, 'x', 18, PALETTE.dim);
    });
    xHit.on('pointerup', () => {
      audio().playSfx('uiTap');
      close();
    });
    card.add([xIcon, xHit]);
  }

  // entrance: 200ms scale .92→1 (gated: reduced-motion users get an instant, calm appearance)
  if (motionAllowed()) {
    card.setScale(0.92).setAlpha(0);
    scene.tweens.add({ targets: card, scale: 1, alpha: 1, duration: 200, ease: 'Back.easeOut' });
  }

  // S8 #24 — shared height application: redraw the bg to `newH` from the FIXED top edge (so already-placed
  // top-anchored content never shifts WITHIN the card) and RE-CENTRE the shrunk card in the viewport via
  // cyFor (both orientations). relayout() (async-content panels) and the fullBleed auto-hug both route
  // through this, so every path lands the same centred, content-hugging result — no dead veil band below.
  const bodyTopLocal = topLocal + 64;
  let compressed = false;
  const applyHeight = (newH: number): { cardH: number; bodyTop: number; footerY: number } => {
    const avail = H - topPad - botPad;
    // S9 FIX-C (G0 AMATEUR 2 — store legal links bisected at 1280x800, systemic): content TALLER than
    // the tallest card used to run straight PAST the clamped bottom border (the border bisected the
    // Restore/Privacy/Terms glyphs — legal text must never clip). OVERFLOW GUARD: draw the bg tall
    // enough to ENCLOSE the content, then uniformly scale the whole card down (<4% on the store at
    // 1280x800 — imperceptible) so border + content fit the viewport together, preserving the internal
    // bottom cushion. The entrance tween is killed first — it would tween scale back to 1 and re-overflow.
    if (newH > avail + 2) {
      compressed = true;
      drawBg(newH);
      scene.tweens.killTweensOf(card);
      const s = avail / newH;
      card.setScale(s).setAlpha(1);
      card.setY(topPad - s * topLocal); // top-anchor the scaled card at topPad
      return { cardH: newH, bodyTop: bodyTopLocal, footerY: topLocal + newH - 38 };
    }
    if (compressed) {
      // A later relayout shrank the content back into range — undo the compression.
      compressed = false;
      card.setScale(1);
    }
    const clamped = Phaser.Math.Clamp(newH, headerBand + 132, avail);
    drawBg(clamped);
    // drawBg keeps the TOP edge fixed at `topLocal` (so already-placed top-anchored content never shifts),
    // which makes the shrunk bg ASYMMETRIC about the card origin — so we cannot reuse cyFor here (it assumes
    // a bg centred on the origin). Instead place the bg's visual mid-point (topLocal + clamped/2) at screen
    // centre, clamped so the top can't cross topPad and a near-full card degrades back to the top anchor.
    const centeredY = H / 2 - topLocal - clamped / 2;
    card.setY(Phaser.Math.Clamp(centeredY, topPad - topLocal, H - botPad - topLocal - clamped));
    return { cardH: clamped, bodyTop: bodyTopLocal, footerY: topLocal + clamped - 38 };
  };
  let relayoutCalled = false;
  const relayout = (contentHeight: number): { cardH: number; bodyTop: number; footerY: number } => {
    relayoutCalled = true;
    if (!(fb || hug)) return { cardH, bodyTop: bodyTopLocal, footerY: cardH / 2 - 38 };
    // S9 FIX-C (G0 AMATEUR 2 root cause): every relayout caller measures contentHeight FROM bodyTop
    // (`contentBottom - bodyTop`), so the card height must be converted with the REAL bodyTop offset
    // (topLocal+64) — the old `headerBand +` math undersized HEADERLESS panels (headerBand 20) by
    // 44px, which is exactly why today-gauntlet's footer caption sat on/past the bottom border.
    // Also: a 22px internal bottom-cushion floor (H*0.028 alone is thinner on short viewports).
    return applyHeight(bodyTopLocal - topLocal + contentHeight + Math.max(22, Math.round(H * 0.028)));
  };

  // S8 #24 — AUTO-HUG for fullBleed panels that do NOT self-manage height (Store/Offline/Credits/…): after
  // the caller synchronously fills the card, measure the real content bottom and shrink+recentre to hug it,
  // collapsing the dead lower band with ZERO per-panel edits. Skipped for: content-measured (hug) cards,
  // panels that already relayout()'d, masked scroll lists (achievements — geometry derived from card
  // bounds), and any panel whose content genuinely FILLS the height (dead band < 12% of the viewport ⇒
  // untouched, e.g. Stats which measured 0% dead) so a full panel's pinned footer is never orphaned.
  if (fb && !hug) {
    scene.time.delayedCall(0, () => {
      if (!card.active || relayoutCalled || card.mask) return;
      if (card.list.some((c) => (c as { mask?: unknown }).mask)) return; // masked scroll surface
      // Measure at unit scale — the entrance tween may be mid-flight (scale ≈0.92); restore after.
      const sx = card.scaleX, sy = card.scaleY, ca = card.alpha;
      card.setScale(1, 1).setAlpha(1);
      let maxBottom = topLocal + headerBand;
      for (const ch of card.list) {
        if (ch === bg || ch === topRail) continue;
        const b = (ch as Partial<Phaser.GameObjects.Components.GetBounds>).getBounds?.();
        if (b && Number.isFinite(b.bottom)) maxBottom = Math.max(maxBottom, b.bottom - card.y);
      }
      card.setScale(sx, sy).setAlpha(ca);
      // S9 FIX-C: 20px cushion floor, and route OVERFLOWING content (fitted > cardH) through
      // applyHeight's new overflow guard so a border can never bisect content on ANY fullBleed panel.
      const fitted = maxBottom - topLocal + Math.max(20, Math.round(H * 0.026));
      if (cardH - fitted >= H * 0.12 || fitted > cardH) applyHeight(fitted);
    });
  }

  return { layer, card, cardW, cardH, bodyTop: bodyTopLocal, footerY: cardH / 2 - 38, close, relayout };
}

/** A bottom-anchored sheet (the share/brag surface). Same close/refcount contract as modal(). */
export function sheet(
  scene: Phaser.Scene,
  opts: { title: string; heightFrac?: number; contentHeight?: number; onClose?: () => void; veilClose?: boolean } = { title: '' },
): Modal {
  const W = scene.scale.width;
  const H = scene.scale.height;
  const cardW = Math.min(W, 460);
  // S3 item 17: content-hugging — when the caller passes its measured content height, the sheet is
  // exactly that tall (clamped to the viewport) so there is no dead band below the last action row.
  const cardH =
    opts.contentHeight !== undefined
      ? Math.min(H * 0.92, opts.contentHeight)
      : Math.min(H * (opts.heightFrac ?? 0.6), 760);

  const layer = scene.add.container(0, 0).setDepth(30_000);
  openModalCount++;
  audio().playPanelOpen(); // #61: the shared panel-open whoosh (fires as any modal()/sheet() surface opens)
  hideDomInputsForModal();
  layer.once(Phaser.GameObjects.Events.DESTROY, () => {
    openModalCount = Math.max(0, openModalCount - 1);
  });
  // Deeper veil (0.98) so the sheet OWNS focus — at 0.95 the result screen ghosted through behind it.
  const veil = scene.add.rectangle(W / 2, H / 2, W, H, PALETTE.void, 0.98).setInteractive();
  const card = scene.add.container(W / 2, H - cardH / 2);
  const r = 24;
  const bg = scene.add.graphics();
  bg.fillStyle(PALETTE.surface, 1);
  bg.fillRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, { tl: r, tr: r, bl: 0, br: 0 });
  bg.lineStyle(1.5, PALETTE.lineStrong, 1);
  bg.strokeRoundedRect(-cardW / 2, -cardH / 2, cardW, cardH, { tl: r, tr: r, bl: 0, br: 0 });
  // grab handle
  const handle = scene.add.graphics();
  handle.fillStyle(PALETTE.lineStrong, 1);
  handle.fillRoundedRect(-21, -cardH / 2 + 8, 42, 5, 2.5);
  const title = scene.add
    .text(0, -cardH / 2 + 34, (opts.title || '').toUpperCase(), {
      fontFamily: FONT.display,
      fontSize: `${typePx(scene, 'title', { min: 17, max: 17 })}px`,
      color: hex(PALETTE.text),
      fontStyle: 'bold',
    })
    .setOrigin(0.5);
  card.add([bg, handle, title]);
  layer.add([veil, card]);

  const close = (): void => {
    layer.destroy();
    opts.onClose?.();
  };
  if (opts.veilClose !== false) veil.on('pointerup', close);

  if (motionAllowed()) {
    card.setY(H + cardH / 2);
    scene.tweens.add({ targets: card, y: H - cardH / 2, duration: 240, ease: 'Cubic.easeOut' });
  }
  // Sheets size to their content at creation (opts.contentHeight); no post-hoc resize is needed, so
  // relayout just reports the current geometry (satisfies the shared Modal contract).
  const relayout = (): { cardH: number; bodyTop: number; footerY: number } => ({
    cardH,
    bodyTop: -cardH / 2 + 64,
    footerY: cardH / 2 - 38,
  });
  return { layer, card, cardW, cardH, bodyTop: -cardH / 2 + 64, footerY: cardH / 2 - 38, close, relayout };
}

/** A left-label / right-value row added to a card at local y. Returns the value Text for live updates. */
export function statRow(
  scene: Phaser.Scene,
  card: Phaser.GameObjects.Container,
  cardW: number,
  y: number,
  label: string,
  value: string,
  valueColor: number = PALETTE.correct,
): Phaser.GameObjects.Text {
  const fs = `${typePx(scene, 'body', { vw: 0.026, min: 13 })}px`;
  const lbl = scene.add
    .text(-cardW / 2 + 22, y, label, { fontFamily: FONT.family, fontSize: fs, color: hex(PALETTE.textDim) })
    .setOrigin(0, 0.5);
  const val = scene.add
    .text(cardW / 2 - 22, y, value, { fontFamily: FONT.family, fontSize: fs, color: hex(valueColor), fontStyle: 'bold' })
    .setOrigin(1, 0.5);
  card.add([lbl, val]);
  return val;
}
